<i18n src="@/locales/models/packing_house_certification.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/agro_exporter/certifications/providers/edit_packing_house_certification.yml"
  lang="yaml"
></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_packing_house')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      packing_house_id: packing_house_id
    }"
  />

  <bread-crumbs :labels="[module_name, packing_house?.name]"></bread-crumbs>

  <alert-danger :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-8 divide-y divide-slate-200">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.packing_house_certification.certification_id") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="certifications.length > 0"
                  :options="certifications"
                  v-model="packing_house_certification.certification_id"
                  :class="isInvalidSelect('certification')"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label
                :class="['label-form', isInvalidLabel('certification_code')]"
              >
                {{ $t("model.packing_house_certification.certification_code") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model="packing_house_certification.certification_code"
                  :class="['input-text', isInvalid('certification_code')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.packing_house_certification.renewal_date") }}
              </label>
              <div class="mt-1">
                <date-form v-model="packing_house_certification.renewal_date" />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.packing_house_certification.expiration_date") }}
              </label>
              <div class="mt-1">
                <date-form
                  v-model="packing_house_certification.expiration_date"
                  :class="isInvalid('certification_code')"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.packing_house_certification.certificate_file") }}
              </label>
              <div class="mt-1">
                <input
                  type="file"
                  class="input-text border p-1"
                  accept=".doc, .docx, .pdf, image/*"
                  @change="setFile($event.target.files[0])"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <div class="mt-1">
                <switch-form
                  v-model="packing_house_certification.active"
                  :label="$t('model.packing_house_certification.active')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowPackingHouse()"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="update()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/certifications/providers/manage_packing_house_certification.js";

export default {
  name: "EditPackingHouseCertification",
  components: {},
  data() {
    return {
      load_general_data: false,
      packing_house: {},
      packing_house_certification: {},
      proc_save_button: false,
      certifications: [],
      certificate_file: null
    };
  },
  props: {
    agro_exporter_id: {},
    packing_house_id: {},
    packing_house_certification_id: {}
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.loadData())
    );
  },
  methods: {
    loadData: async function () {
      this.fetchPackingHouse();
      await this.fetchCertifications();
      await this.fetchPackingHouseCertification();
      this.load_general_data = true;
    },
    fetchPackingHouse: async function () {
      let response = await Requester.fetchPackingHouse(
        this.agro_exporter_id,
        this.packing_house_id
      );

      this.packing_house = response.data;
      if (!this.packing_house.manage_certifications)
        this.goToShowPackingHouse();
    },
    fetchCertifications: async function () {
      let response = await Requester.fetchCertifications(
        this.agro_exporter_id,
        this.packing_house_id
      );

      this.certifications = response.data;
    },
    async fetchPackingHouseCertification() {
      let response = await Requester.fetchPackingHouseCertification(
        this.agro_exporter_id,
        this.packing_house_id,
        this.packing_house_certification_id
      );
      this.packing_house_certification = response.data;
    },
    setFile: function (new_file) {
      this.certificate_file = new_file;
    },
    update: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.update(
          this.agro_exporter_id,
          this.packing_house_id,
          this.packing_house_certification_id,
          {
            ...this.packing_house_certification,
            certificate_file: this.certificate_file
          }
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowPackingHouse();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_save_button = false;
    },
    goToShowPackingHouse() {
      this.$router.push({
        name: this.routeName("show_packing_house"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          packing_house_id: this.packing_house_id
        }
      });
    }
  }
};
</script>
